import React, { useState, useRef } from 'react';
import { adminApi, BACKEND_URL } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Plus, Image as ImageIcon, X } from 'lucide-react';

// Shared How-It-Works admin form field editors — used by both the "Add Topic" quick
// -create dialog (AdminHowItWorksPage) and the full editor page
// (AdminHowItWorksEditorPage), so the two stay in sync instead of drifting.

// Reused for both the Architecture and Step-by-Step diagrams.
export const ImageUploadField = ({ label, value, onChange, folder }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setUploading(true);
    try {
      const result = await adminApi.uploadImage(file, folder);
      onChange(result.path);
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <Label>{label}</Label>
      {value ? (
        <div className="relative w-fit mt-2">
          <img
            src={`${BACKEND_URL}${value}`}
            alt=""
            className="h-28 w-40 object-cover rounded-sm border border-structure"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
            aria-label="Remove image"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`mt-2 rounded-md border-2 border-dashed p-4 text-center transition-colors ${dragOver ? 'border-autoxium-blue bg-autoxium-blue/5' : 'border-structure'}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => uploadFile(e.target.files?.[0])}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
          >
            <ImageIcon className="h-4 w-4" />
            {uploading ? 'Uploading…' : 'Upload Image'}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">or drag and drop an image here</p>
        </div>
      )}
    </div>
  );
};

// Repeatable single-string rows, used for Step-by-Step steps and Common Failure Signs.
export const StringListField = ({ label, items, onChange, placeholder, addLabel }) => {
  const updateItem = (i, value) => {
    const next = [...items];
    next[i] = value;
    onChange(next);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...items, '']);

  return (
    <div>
      <Label>{label}</Label>
      <div className="space-y-2 mt-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={item} onChange={(e) => updateItem(i, e.target.value)} placeholder={placeholder} />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} aria-label="Remove">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2 gap-2" onClick={addItem}>
        <Plus className="h-4 w-4" /> {addLabel}
      </Button>
    </div>
  );
};

// Repeatable {name, detail} rows, used for Architecture Components.
export const ComponentListField = ({ items, onChange }) => {
  const updateItem = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...items, { name: '', detail: '' }]);

  return (
    <div>
      <Label>Components</Label>
      <div className="space-y-3 mt-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2 rounded-md border border-structure p-3">
            <div className="flex-1 space-y-2">
              <Input
                value={item.name}
                onChange={(e) => updateItem(i, 'name', e.target.value)}
                placeholder="Component name"
              />
              <Textarea
                rows={2}
                value={item.detail}
                onChange={(e) => updateItem(i, 'detail', e.target.value)}
                placeholder="What it does"
              />
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} aria-label="Remove">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2 gap-2" onClick={addItem}>
        <Plus className="h-4 w-4" /> Add Component
      </Button>
    </div>
  );
};
